import React, { useState, useMemo } from 'react';
import { View, LogBox, Dimensions, Modal, ScrollView } from 'react-native';
import { interpolateGnBu, interpolateBlues } from 'd3-scale-chromatic';
import PaperTableDynamic from '../common/controls/TableProvider';
import PieChartComp from '../common/charts/piChart-builder';
import { useAuth } from '../../../providers/AuthProvider';
import { useClient } from '../../../providers/ClientProvider';
import { Loader } from '../../Common/Loader';
import ToastMessage from '../common/controls/Toast';
import {
  filterData,
  handleFilterReset,
  handleExportClick,
  constructData,
  EnableExport,
  CommonStyles,
  GetConsolidatedLog,
  FilterChoices
} from './ConsolidatedController';
import { FilterIcon, ExportIcon, FilterBox, SelectedFilters } from './ReUsableComps';
import formCustomStyles from '../common/forms/formStyles';
import { TabScreen, Tabs, TabsProvider } from 'react-native-paper-tabs';
import { colorType3, colorType5, logStyles } from '../LogControllers/LogStyles';
import LogKpiRow from '../LogControllers/LogKpiRow';
import LogDateRangeFilter from '../common/controls/LogDateRangeFilter';
import processData from '../common/service/dataProcessor';

interface Props {
  siteId: string;
  logTitle: string;
  projectId: string;
  isDashboard?: boolean;
  packageFilter?: string[] | null;
}

import { useQuery } from '@tanstack/react-query';

export default function ConsolRecycledMaterial(props: Props) {
  LogBox.ignoreLogs(["EventEmitter.removeListener"]);

  const { getAccessToken } = useAuth();
  const { clientAPIURL } = useClient();

  const { data: reportData, isLoading } = useQuery({
    queryKey: ['consolidated-log', props.logTitle, props.projectId],
    queryFn: async () => {
      const accessToken = await getAccessToken();
      const jResponse = await GetConsolidatedLog(props.logTitle, clientAPIURL, accessToken, props.projectId);
      return constructData(jResponse);
    },
  });

  if (isLoading) return <Loader />;

  return reportData && reportData.items && reportData.columns ? (
    <CreateNoiseLogCharts
      LogData={reportData.items}
      LogColumns={reportData.columns}
      siteId={props.siteId}
      logTitle={props.logTitle}
      isDashboard={props.isDashboard}
      packageFilter={props.packageFilter}
    />
  ) : null;
}

interface CreateNoiseLogChartsProps {
  LogData: any;
  LogColumns: any;
  siteId: string;
  logTitle: string;
  isDashboard?: boolean;
  packageFilter?: string[] | null;
}

const CreateNoiseLogCharts: React.FC<CreateNoiseLogChartsProps> = ({ LogData, LogColumns, siteId, logTitle, isDashboard, packageFilter }) => {
  const [FilterVisible, setFilterVisible] = React.useState(false);
  const screenWidth = Dimensions.get('window').width;

  const ColumnsWithIDColumn = LogColumns && [
    ...LogColumns,
    {
      id: 'CODE_GENERATED_ID',
      title: 'Item ID',
      columnName: 'ID',
      list: '7ae860e9-6c67-46d0-b65e-f2869542a3aa',
      datatype: 'calculated',
      createdAt: 'YOUR_CREATED_AT_TIMESTAMP',
      modifiedAt: 'YOUR_MODIFIED_AT_TIMESTAMP',
      required: false,
      subType: 'Float',
      choiceChoices: null,
      choiceType: null,
      choiceMultiselect: null,
      multiline: false,
      order: 0,
      calculatedFormula: null,
      listNavigation: null,
    }
  ];

  const updatedData = useMemo(() => {
    const raw = processData(logTitle, LogData);
    if (!packageFilter?.length) return raw;
    return raw.filter((item: any) => packageFilter.includes(item.packageName));
  }, [logTitle, LogData, packageFilter]);

  const [selectedYear, setSelectedYear] = useState<string>('ALL');
  const [selectedMonth, setSelectedMonth] = useState<string>('ALL');
  const [selectedPackage, setSelectedPackage] = useState<string>('ALL');
  const [toastVisibility, setToastVisibility] = useState<boolean>(false);
  const [toastText, setToastText] = useState<string>('');
  const [newData, setNewData] = useState<any[]>(updatedData);
  const [dateRangeFiltered, setDateRangeFiltered] = useState<any[]>(updatedData);

  function handleYearFilterSelect(val: string) {
    setSelectedYear(val);
    filterData(val, selectedMonth, selectedPackage, setToastVisibility, updatedData, setToastText, setNewData);
  }
  function handleMonthFilterSelect(val: string) {
    setSelectedMonth(val);
    filterData(selectedYear, val, selectedPackage, setToastVisibility, updatedData, setToastText, setNewData);
  }
  function handlePackageFilterSelect(val: string) {
    filterData(selectedYear, selectedMonth, val, setToastVisibility, updatedData, setToastText, setNewData);
    setSelectedPackage(val);
  }
  function handleToastDismiss() {
    setToastVisibility(false);
  }

  const uniquePackagesArray = [...new Set(updatedData.map((item: any) => item.packageName))];
  const packageFilterOptions = FilterChoices(uniquePackagesArray, 'Package');
  const yearFilterOptions = FilterChoices([...new Set(updatedData.map((item: any) => item.Year))], 'Year');
  const monthFilterOptions = FilterChoices([...new Set(updatedData.map((item: any) => item.Month))], 'Month');

  const { byCSITypePieData, byCSITotalValuePieData, kpiItems } = useMemo(() => {
    const pieAccum1: Record<string, number> = {};
    dateRangeFiltered.forEach((item: any) => {
      if (item.CSI == null) return;
      const k = String(item.CSI);
      pieAccum1[k] = (pieAccum1[k] ?? 0) + 1;
    });
    const byCSITypePieData = Object.entries(pieAccum1).map(([name, value], index) => ({
      name, value, color: colorType5(index.toString()),
    }));

    const pieAccum2: Record<string, number> = {};
    dateRangeFiltered.forEach((item: any) => {
      if (item.CSI == null) return;
      const k = String(item.CSI);
      pieAccum2[k] = (pieAccum2[k] ?? 0) + (Number(item.TotalValueOfRecycleContentMaterial) || 0);
    });
    const byCSITotalValuePieData = Object.entries(pieAccum2).map(([name, value], index) => ({
      name, value: Math.round(value), color: colorType3(index.toString()),
    }));

    const TotalCount = dateRangeFiltered.reduce((total: number, item: any) => {
      return total + (Number(item.TotalValueOfRecycleContentMaterial) || 0);
    }, 0);

    const kpiItems = [
      { label: 'Total Value', value: Math.round(TotalCount), color: '#00695C' },
      { label: 'Entries', value: dateRangeFiltered.length, color: '#4527A0' },
    ];

    return { byCSITypePieData, byCSITotalValuePieData, kpiItems };
  }, [dateRangeFiltered]);

  return (
    <View style={logStyles.logRoot}>
      <View style={CommonStyles.ribbonIconContainer}>
        <View style={CommonStyles.IconContainer}>
          <FilterIcon setFilterVisible={setFilterVisible} FilterVisible={FilterVisible} />
        </View>
        <View style={CommonStyles.IconContainer}>
          {EnableExport() === true
            ? <ExportIcon handleExportClick={handleExportClick} newData={newData} />
            : null
          }
        </View>
        <SelectedFilters selectedMonth={selectedMonth} selectedPackage={selectedPackage} selectedYear={selectedYear} />
      </View>
      <Modal
        animationType="fade"
        transparent={true}
        visible={FilterVisible}
        onRequestClose={() => { setFilterVisible(!FilterVisible); }}>
        <View style={formCustomStyles.centeredView}>
          <FilterBox
            FilterVisible={FilterVisible}
            setFilterVisible={setFilterVisible}
            screenWidth={screenWidth}
            CommonStyles={CommonStyles}
            yearFilterOptions={yearFilterOptions}
            handleYearFilterSelect={handleYearFilterSelect}
            selectedYear={selectedYear}
            monthFilterOptions={monthFilterOptions}
            handleMonthFilterSelect={handleMonthFilterSelect}
            selectedMonth={selectedMonth}
            packageFilterOptions={packageFilterOptions}
            handlePackageFilterSelect={handlePackageFilterSelect}
            selectedPackage={selectedPackage}
            handleFilterReset={handleFilterReset}
            setSelectedMonth={setSelectedMonth}
            setSelectedYear={setSelectedYear}
            setSelectedPackage={setSelectedPackage}
            setNewData={setNewData}
            updatedData={updatedData}
          />
        </View>
      </Modal>
      <View>
        <ToastMessage
          show={toastVisibility}
          actionLabel="Dismiss"
          toastMessage={toastText}
          duration={3000}
          onDismiss={handleToastDismiss}
        />
      </View>
      <View style={logStyles.logRoot}>
        {isDashboard ? (
          <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
            <View style={{ zIndex: 200 }}>
              <LogKpiRow items={kpiItems}>
                <LogDateRangeFilter data={newData} onFilterChange={setDateRangeFiltered} />
              </LogKpiRow>
            </View>
            <View>
              <View style={[CommonStyles.container, CommonStyles.divider]}>
                <View key={1} style={[CommonStyles.card, { backgroundColor: '' }]}>
                  {/* PieChartComp contract: data[].name=string (non-null), data[].value=number, data[].color=string */}
                  <PieChartComp data={byCSITypePieData} colorPallate={interpolateBlues} subTitle={'(%)'} ChartTitle={"CSI Type (%)"} />
                </View>
                <View key={2} style={[CommonStyles.card, { backgroundColor: '' }]}>
                  <PieChartComp data={byCSITotalValuePieData} colorPallate={interpolateGnBu} subTitle={'(Recycled Material (%) / CSI)'} ChartTitle={"Total Value"} />
                </View>
              </View>
            </View>
          </ScrollView>
        ) : (
          <TabsProvider defaultIndex={0}>
            <Tabs style={{ backgroundColor: '#fff', height: "100%" }} showLeadingSpace={true}>
              <TabScreen label="Charts" icon="chart-bar">
                <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
                  <View style={{ zIndex: 200 }}>
                    <LogKpiRow items={kpiItems}>
                      <LogDateRangeFilter data={newData} onFilterChange={setDateRangeFiltered} />
                    </LogKpiRow>
                  </View>
                  <View>
                    <View style={[CommonStyles.container, CommonStyles.divider]}>
                      <View key={1} style={[CommonStyles.card, { backgroundColor: '' }]}>
                        <PieChartComp data={byCSITypePieData} colorPallate={interpolateBlues} subTitle={'(%)'} ChartTitle={"CSI Type (%)"} />
                      </View>
                      <View key={2} style={[CommonStyles.card, { backgroundColor: '' }]}>
                        <PieChartComp data={byCSITotalValuePieData} colorPallate={interpolateGnBu} subTitle={'(Recycled Material (%) / CSI)'} ChartTitle={"Total Value"} />
                      </View>
                    </View>
                  </View>
                </ScrollView>
              </TabScreen>
              <TabScreen label="List" icon="database">
                <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
                  <View>
                    <View style={CommonStyles.container}>
                      <View style={CommonStyles.cardThree}>
                        {dateRangeFiltered && dateRangeFiltered.length > 0 && ColumnsWithIDColumn && (
                          <PaperTableDynamic
                            data={dateRangeFiltered}
                            columns={ColumnsWithIDColumn}
                            siteId={siteId}
                            logTitle={logTitle}
                            updateData={() => {}}
                            isConsolidatedTable={true}
                          />
                        )}
                      </View>
                    </View>
                  </View>
                </ScrollView>
              </TabScreen>
            </Tabs>
          </TabsProvider>
        )}
      </View>
    </View>
  );
};
